import { useState, useEffect } from 'react';
import { apiService } from '../../services/ApiService';
import { showErrorToast, showSuccessToast } from '../../components/shared/toast';
import type { Asset } from '../../services/AssetService';

interface UseSyncPipelineParams {
  selectedAsset: Asset | null;
  isAuthenticated: boolean;
  enterViewerPage: (options?: { mode?: '3D' | 'Panorama' | '2D'; hideOtherViewers?: boolean }) => void;
  setRequestedModelFileName: (fileName: string | null) => void;
  setShow3DViewer: (show: boolean) => void;
  setHasRendered3DViewer: (rendered: boolean) => void;
}

/**
 * Sync task queue (auto-assign on sync): pending-task fetch, dismiss/apply
 * handlers, and the post-extraction reassignment pipeline — extracted verbatim
 * from App.tsx (F-2). Viewer-transition plumbing stays injected
 * (enterViewerPage + the 3D-viewer setters) because it belongs to the viewer
 * stack, not to sync. App destructures the return under the original names.
 */
export const useSyncPipeline = ({
  selectedAsset,
  isAuthenticated,
  enterViewerPage,
  setRequestedModelFileName,
  setShow3DViewer,
  setHasRendered3DViewer,
}: UseSyncPipelineParams) => {
  // Sync task queue state (auto-assign on sync)
  const [pendingSyncTasks, setPendingSyncTasks] = useState<Array<{ id: string; sync_file_name: string; created_at: string }>>([]);
  const [syncPipelineRunning, setSyncPipelineRunning] = useState(false);
  const [syncPipelineStatus, setSyncPipelineStatus] = useState('');

  // Fetch pending sync tasks when asset changes
  useEffect(() => {
    if (selectedAsset?.id && isAuthenticated) {
      apiService.get(`/assets/${selectedAsset.id}/sync-tasks/pending`)
        .then((tasks: any) => {
          setPendingSyncTasks(Array.isArray(tasks) ? tasks : []);
        })
        .catch(() => {
          setPendingSyncTasks([]);
        });
    } else {
      setPendingSyncTasks([]);
    }
  }, [selectedAsset?.id, isAuthenticated]);

  // Sync pipeline: dismiss a pending task
  const handleDismissSyncTask = async (taskId: string) => {
    if (!selectedAsset) return;
    try {
      await apiService.post(`/assets/${selectedAsset.id}/sync-tasks/${taskId}/dismiss`, {});
      setPendingSyncTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (err) {
      // The banner stays on screen when the dismiss is refused, which reads as a dead button.
      // Say why, or the user just keeps clicking it.
      console.error('Failed to dismiss sync task:', err);
      showErrorToast('Failed to dismiss the sync notification. Please try again.');
    }
  };

  // Sync pipeline: Apply - delete data, switch to viewer, load model
  const handleApplySyncTask = async (taskId: string) => {
    if (!selectedAsset) return;
    setSyncPipelineRunning(true);
    setSyncPipelineStatus('Deleting existing assets and locations...');
    try {
      // Step 1: Delete all POIs
      await apiService.delete(`/assets/${selectedAsset.id}/pois`);
      // Step 2: Delete all locations
      await apiService.delete(`/assets/${selectedAsset.id}/locations`);

      setSyncPipelineStatus('Data cleared. Loading model...');

      // Step 3: Switch to viewer tab (canonical transition — adds refresh-restore persistence)
      enterViewerPage();

      // Step 4: Request model load (use first IFC file)
      if (selectedAsset.files.ifc?.length) {
        const firstIfc = selectedAsset.files.ifc[0];
        setRequestedModelFileName(firstIfc.fileName);
        setShow3DViewer(true);
        setHasRendered3DViewer(true);
      }

      setSyncPipelineStatus('Model loading. Extract assets & locations from the model, then click "Run Reassignment".');

      // Remove this task from pending list (keep it in DB as 'pending' until reassignment runs)
    } catch (err) {
      console.error('Failed to apply sync task:', err);
      setSyncPipelineStatus('Pipeline failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Sync pipeline: Run reassignment with saved config after extraction
  const handleRunReassignment = async () => {
    if (!selectedAsset) return;
    setSyncPipelineStatus('Running location reassignment...');
    try {
      // Fetch saved config
      const config = await apiService.get(`/assets/${selectedAsset.id}/assignment-config`);

      // Run location reassignment if config exists
      if (config.location_method === 'properties' && (config.storey_property || config.space_property)) {
        setSyncPipelineStatus('Reassigning locations...');
        await apiService.post(`/assets/${selectedAsset.id}/pois/reassign-locations`, {
          storeyProperty: config.storey_property,
          spaceProperty: config.space_property,
          useSpatialMatching: true
        });
      } else if (config.location_method === 'geolocation') {
        setSyncPipelineStatus('Assigning by geolocation...');
        await apiService.post(`/assets/${selectedAsset.id}/pois/assign-by-geolocation`, {});
      }

      // Run systems reassignment if config exists
      if (config.system_property) {
        setSyncPipelineStatus('Reassigning systems...');
        await apiService.post(`/assets/${selectedAsset.id}/pois/reassign-systems`, {
          systemProperty: config.system_property,
          subcategoryLevels: config.subcategory_levels || []
        });
      }

      // Mark all pending sync tasks as completed
      const unresolvedTasks: typeof pendingSyncTasks = [];
      for (const task of pendingSyncTasks) {
        try {
          await apiService.post(`/assets/${selectedAsset.id}/sync-tasks/${task.id}/complete`, {});
        } catch (e) {
          console.warn('Failed to mark sync task complete:', e);
          unresolvedTasks.push(task);
        }
      }

      // The reassignment itself succeeded here — only the bookkeeping can still fail. Clearing the
      // list unconditionally hid tasks the server still has open, so the banner disappeared while
      // the next sync kept re-raising them. Keep the ones that did not close and say so.
      if (unresolvedTasks.length > 0) {
        setPendingSyncTasks(unresolvedTasks);
        setSyncPipelineStatus(`Reassignment finished, but ${unresolvedTasks.length} sync task(s) could not be closed. Run it again to retry.`);
        showErrorToast(`Reassignment finished, but ${unresolvedTasks.length} sync task(s) could not be marked complete.`);
        return; // leave the pipeline banner up — it is the only surface showing that status
      }

      setPendingSyncTasks([]);
      setSyncPipelineRunning(false);
      setSyncPipelineStatus('');
      showSuccessToast('Reassignment complete! Locations and systems have been updated.');
    } catch (err) {
      console.error('Failed to run reassignment:', err);
      setSyncPipelineStatus('Reassignment failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  return {
    pendingSyncTasks,
    syncPipelineRunning, setSyncPipelineRunning,
    syncPipelineStatus, setSyncPipelineStatus,
    handleDismissSyncTask,
    handleApplySyncTask,
    handleRunReassignment,
  };
};
